import React, { memo, useEffect, useState } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { reduxForm } from "redux-form";
import { createStructuredSelector } from "reselect";
import {
  fetchReferralCode,
  clearData,
  setData,
  fetchPlan,
  selectPlan,
  changeFormField
} from "ducks/actions";
import {
  makeSelectAddons,
  makeSelectPreSelectedAddons,
  makeSelectDefaultPlanAddons,
  makeSelectPayMonthlyItems,
  makeSelectPayTodayItems,
  makeSelectReferralCode,
  makeSelectDefaultPromoCodeOffers,
  makeSelectPlanFAQ,
  makeSelectPlanName,
  makeSelectEligiblePlans,
  makeSelectPlanDetails,
  makeSelectIccIdDetails,
  makeSelectIsFetchingData,
  makeSelectFormattedSelectedNumber,
  makeSelectDeliveryDetailsItems,
  makeSelectValidateIccId,
  makeSelectIsUserLoggedIn,
  makeSelectSelectedSimType,
  makeSelectPrecheckoutInfo,
  makeSelectFetchPlanCounter,
  makeSelectSelectedNumberType,
  makeSelectNumberType,
  makeSelectThemeColors,
  makeSelectPayDevice,
  makeSelectShow5GAddon,
  makeSelectShowDeviceCompatibilityChecker,
  makeSelectShow5GDeviceCompatibilityChecker,
  makeSelectPreselected5GAddon,
  makeSelectIs5GAddonSelected,
  makeSelectIs5GCompatible,
  makeSelectEnableNextButtonWithDeviceChecker,
  makeSelectHideComponentsWithDeviceChecker,
  makeSelectSelectedMobileDeviceBrand,
  makeSelectFilteredMobileDeviceModels,
  makeSelectSelectedMobileDevice,
  makeSelectIs5GBundleAddonSelected,
  makeSelectEnableInternationalComms
} from "ducks/selectors";
import SmartRender from "containers/SmartRender";
import { forms, SIM_TYPES } from "utils/constants";
import Spinner, { styles } from "components/Spinner";
import { getURLParamValue, urlParam, getProductIdFromUrl } from "utils/helpers";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import head from "lodash/head";
import find from "lodash/find";

const PreCheckoutHOC = Component =>
  function Comp(props) {
    const {
      match,
      fetchPlan,
      selectPlan,
      isFetchingPlan,
      isFetchingEligiblePlans,
      validateReferralCode,
      eligiblePlans,
      planDetails,
      orderDetails: { selected_referral_code },
      changeField,
      isEsimEnable,
      fetchPlanCounter
    } = props;
    const pvid = getProductIdFromUrl();
    const selectedPlanName = get(planDetails, "name");
    const { channel } = get(match, "params", {});
    const plan_name = urlParam("plan_name");
    const [showSpinner, setShowSpinner] = useState(true);
    const planParams = {};
    if (channel) {
      planParams["channel"] = channel;
    }
    if (plan_name) {
      planParams["plan_name"] = plan_name;
    }
    useEffect(() => {
      changeField("_fetch_plan_counter.max", 1);
    }, [changeField]);

    useEffect(
      () => {
        const choosePlan =
          isEmpty(planDetails) || (plan_name && planDetails.name !== plan_name);

        let acc = get(fetchPlanCounter, "acc"),
          max = get(fetchPlanCounter, "max");
        let stopCondition =
          isEmpty(eligiblePlans) &&
          !isFetchingPlan &&
          !isFetchingEligiblePlans &&
          isEmpty(planDetails) &&
          acc === max;
        let proceed = !stopCondition;

        if (stopCondition) {
          setShowSpinner(stopCondition);
          proceed = !stopCondition;
        } else {
          setShowSpinner(isFetchingPlan);
        }

        if (proceed) {
          if (choosePlan && !isFetchingPlan && !isFetchingEligiblePlans) {
            if (isEmpty(eligiblePlans)) {
              fetchPlan(planParams);
              changeField("_fetch_plan_counter.acc", acc + 1);
            } else {
              const urlPlan = plan_name
                ? find(eligiblePlans, { name: plan_name })
                : undefined;
              const selectedPlan = !isEmpty(urlPlan)
                ? urlPlan
                : head(eligiblePlans);
              selectPlan(selectedPlan);
            }
          }
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        eligiblePlans,
        planDetails,
        isFetchingPlan,
        isFetchingEligiblePlans,
        plan_name,
        fetchPlanCounter
      ]
    );

    useEffect(() => {
      selected_referral_code &&
        validateReferralCode(selected_referral_code, selectedPlanName, pvid);
    }, [selected_referral_code, validateReferralCode, pvid, selectedPlanName]);

    useEffect(() => {
      if (isEsimEnable && urlParam("sim_type") === SIM_TYPES.ESIM) {
        changeField("sim_type", SIM_TYPES.ESIM);
      }
    }, [isEsimEnable, changeField]);

    return showSpinner ? (
      <Spinner size={200} sx={styles.fullPage} />
    ) : (
      <Component {...props} />
    );
  };

export const mapStateToProps = createStructuredSelector({
  formValues: makeSelectPrecheckoutInfo(),
  addOns: makeSelectAddons(),
  payMonthlyItems: makeSelectPayMonthlyItems(),
  payTodayItems: makeSelectPayTodayItems(),
  referralCode: makeSelectReferralCode(),
  defaultPromoCodeOffers: makeSelectDefaultPromoCodeOffers(),
  planFAQ: makeSelectPlanFAQ(),
  planName: makeSelectPlanName(),
  isFetchingPlan: makeSelectIsFetchingData("planService"),
  isFetchingEligiblePlans: makeSelectIsFetchingData("eligiblePlans"),
  isCreatingOrder: makeSelectIsFetchingData("order"),
  isFetchingPaymentRequest: makeSelectIsFetchingData("paymentRequest"),
  eligiblePlans: makeSelectEligiblePlans(),
  planDetails: makeSelectPlanDetails(),
  iccIdDetails: makeSelectIccIdDetails(),
  addons: makeSelectAddons(),
  orderDetails: makeSelectValidateIccId(),
  selectedNumber: makeSelectFormattedSelectedNumber(),
  selectedSimType: makeSelectSelectedSimType(),
  deliveryDetailsItems: makeSelectDeliveryDetailsItems(),
  defaultAddons: makeSelectPreSelectedAddons(),
  defaultPlanAddons: makeSelectDefaultPlanAddons(),
  isLoggedIn: makeSelectIsUserLoggedIn(),
  fetchPlanCounter: makeSelectFetchPlanCounter(),
  selectedNumberType: makeSelectSelectedNumberType(),
  numberType: makeSelectNumberType(),
  themeColors: makeSelectThemeColors(),
  selectedPhone: makeSelectPayDevice(),
  show5GAddon: makeSelectShow5GAddon(),
  showDeviceCompatibilityChecker: makeSelectShowDeviceCompatibilityChecker(),
  show5GDeviceCompatibilityChecker: makeSelectShow5GDeviceCompatibilityChecker(),
  hidePromo: () => getURLParamValue("hpromo"),
  hideAddon: () => getURLParamValue("haddon"),
  hidePlan: () => getURLParamValue("hplan"),
  hidePayToday: () => getURLParamValue("hpaytoday"),
  isEsimEnable: () => getURLParamValue("esim"),
  initialValues: state => {
    return {
      sim_type: makeSelectSelectedSimType()(state),
      _fetch_plan_counter: makeSelectFetchPlanCounter()(state),
      number_type: makeSelectNumberType()(state)
    };
  },
  preselected5GAddon: makeSelectPreselected5GAddon(),
  is5GAddonSelected: makeSelectIs5GAddonSelected(),
  is5GCompatible: makeSelectIs5GCompatible(),
  enableNextButtonWithDeviceChecker: makeSelectEnableNextButtonWithDeviceChecker(),
  hideComponentsWithDeviceChecker: makeSelectHideComponentsWithDeviceChecker(),
  selectedMobileDeviceBrand: makeSelectSelectedMobileDeviceBrand(),
  filteredMobileDeviceModels: makeSelectFilteredMobileDeviceModels(),
  selectedMobileDevice: makeSelectSelectedMobileDevice(),
  is5GBundleAddon: makeSelectIs5GBundleAddonSelected(),
  displayInternationalComms: makeSelectEnableInternationalComms()
});

export const mapDispatchToProps = dispatch => {
  return {
    fetchPlan: params => dispatch(fetchPlan(params)),
    selectPlan: plan => dispatch(selectPlan(plan)),
    validateReferralCode: (referral_code, plan_name, device_sku) =>
      dispatch(
        fetchReferralCode({
          referral_code,
          plan_name,
          ...(device_sku && { device_sku })
        })
      ),
    resetReferralCode: () =>
      dispatch(clearData(["referralCode", "selectedAddons"])),
    handleSelectAddon: (addonId, selected) =>
      dispatch(
        setData({ key: "selectedAddons", data: { [addonId]: selected } })
      ),
    changeField: (field, value) => {
      dispatch(
        changeFormField({
          form: forms.PRE_CHECKOUT_FORM,
          field: field,
          value: value
        })
      );
    }
  };
};

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(
  withConnect,
  reduxForm({
    form: forms.PRE_CHECKOUT_FORM,
    destroyOnUnmount: false
  }),
  memo
)(PreCheckoutHOC(SmartRender));
